import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, distinctUntilChanged, filter, pipe, switchMap, tap } from 'rxjs';

import { CatalogApi } from '../data/api/catalog.api';
import type { Promos } from '../data/models/catalog/promo.model';
import { NO_PROMOS } from '../shared/utils/promo';

type PromoState = {
  /** Филиал, акции которого нужны UI сейчас; null — филиала нет или меню ещё не загружено. */
  requested: string | null;
  /** Филиал, для которого лежат `promos`; null — ответа ещё не было. */
  loadedFor: string | null;
  promos: Promos;
};

/** Root и без инжекта сторов: его инжектит CartStore, а филиал подаёт StockStore — иначе цикл DI. */
export const PromoStore = signalStore(
  { providedIn: 'root' },
  withState<PromoState>({ requested: null, loadedFor: null, promos: NO_PROMOS }),

  withComputed((state) => ({
    /** Единственная поверхность для UI: акции только запрошенного филиала, чужие — «нет акций». */
    view: computed((): Promos => {
      const requested = state.requested();
      return requested !== null && requested === state.loadedFor() ? state.promos() : NO_PROMOS;
    }),
  })),

  withMethods((store, api = inject(CatalogApi)) => ({
    load: rxMethod<string | null>(
      pipe(
        // Ключ пишем до сети: view сразу прячет акции прежнего филиала.
        tap((requested) => patchState(store, { requested })),
        // null не рвёт полёт: A → null → A (resolve адреса, вход) — не смена филиала.
        filter((branchId): branchId is string => branchId !== null),
        distinctUntilChanged(),
        switchMap((branchId) =>
          api.getPromos(branchId).pipe(
            tap((promos) => patchState(store, { loadedFor: branchId, promos })),
            // catchError во ВНУТРЕННЕМ pipe: подписка живёт, меню остаётся без скидок и без тостов.
            catchError(() => EMPTY),
          ),
        ),
      ),
    ),
  })),
);
